import { memo } from "react";
import { motion } from "framer-motion";
import { pick } from "@/lib/format";
import { DishImage } from "./DishImage";
import { LabelBadges } from "./Badges";
import { Price } from "./Price";

const ANIM = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "0px 0px -40px 0px" },
  transition: { duration: 0.45, ease: [0.2, 0.8, 0.2, 1] },
};

export const MenuCard = memo(function MenuCard({ item, lang, s, t, onOpen }) {
  const title = pick(item, "title", lang);
  const desc = pick(item, "description", lang);
  const style = s.menu.card_style;
  const horizontal = style === "horizontal";
  const minimal = style === "minimal";
  const Wrapper = s.animations.enabled ? motion.article : "article";
  const sizes = horizontal ? "160px" : "(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw";

  return (
    <Wrapper {...(s.animations.enabled ? ANIM : {})} data-anchor={`item-${item.id}`} className={`group h-full ${s.animations.hover ? "wt-card-hover" : ""}`} style={{ borderRadius: "var(--wt-radius)" }}>
      <button
        type="button"
        onClick={() => onOpen(item.id)}
        data-testid={`menu-card-${item.id}`}
        className={`flex h-full w-full overflow-hidden text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wt-primary)] ${horizontal ? "flex-row" : "flex-col"}`}
        style={{
          background: minimal ? "transparent" : "var(--wt-card)",
          borderRadius: "var(--wt-radius)",
          border: minimal ? "none" : "1px solid color-mix(in srgb, var(--wt-text) 7%, transparent)",
        }}
      >
        <div
          className={`wt-img-zoom relative shrink-0 overflow-hidden ${horizontal ? "w-28 self-stretch sm:w-40" : "w-full"}`}
          style={{ aspectRatio: horizontal ? undefined : "var(--wt-aspect)", minHeight: horizontal ? 112 : undefined, borderRadius: minimal ? "var(--wt-radius)" : 0, background: "var(--wt-surface)" }}
        >
          <DishImage url={item.image_url} alt={title.text} sizes={sizes} size={horizontal ? "sm" : "md"} />
          <LabelBadges labels={item.labels} t={t} className="absolute start-2 top-2 max-w-[90%]" />
          {!item.is_available && (
            <div className="absolute inset-0 grid place-items-center bg-black/55">
              <span data-testid={`unavailable-badge-${item.id}`} className="rounded-full bg-black/75 px-3 py-1 text-xs font-bold text-white">
                {t("pub.unavailable")}
              </span>
            </div>
          )}
        </div>
        <div className={`flex min-w-0 flex-1 flex-col gap-1.5 ${minimal ? "px-1 pb-1 pt-3" : "p-4"}`}>
          <h3 lang={title.lang} dir={title.fallback ? "auto" : undefined} data-testid={`menu-card-title-${item.id}`} className="line-clamp-2 break-words text-base font-bold leading-snug transition-colors group-hover:text-[var(--wt-primary)] sm:text-lg">
            {title.text}
          </h3>
          {s.menu.show_descriptions && desc.text && (
            <p lang={desc.lang} dir={desc.fallback ? "auto" : undefined} className="line-clamp-2 break-words text-sm leading-relaxed text-[var(--wt-muted)]">
              {desc.text}
            </p>
          )}
          <div className="mt-auto pt-2">
            <Price item={item} s={s} lang={lang} testid={`menu-card-price-${item.id}`} />
          </div>
        </div>
      </button>
    </Wrapper>
  );
});
